import { FC } from 'react'
import { Dialog, DialogHeader, DialogBody, Spinner, Typography, IconButton } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { AiOutlinePlus } from 'react-icons/ai'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAgamiRoutes } from '../hooks/useAgamiRoutes'
import Center from '../components/Center'
import { AddAgamiToExperienceTable } from './AddAgamiToExperienceTable'
import { AgamiSpot } from '../types/supabase'

interface AddAgamiToExperienceDialogProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  currentExpId: string
  isLoading: boolean
  agamiList: AgamiSpot[]
  refetchAfterChange: () => Promise<void>
}

export const AddAgamiToExperienceDialog: FC<AddAgamiToExperienceDialogProps> = ({
  isOpen,
  setIsOpen,
  currentExpId,
  isLoading,
  agamiList,
  refetchAfterChange,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // The flag routing lives in the helper, not here (06.4): this button is the
  // historic "create from the experience" entry point, and it keeps working
  // identically for everyone — only the destination form changes with the flag.
  const { newAgamiPath } = useAgamiRoutes()
  return (
    <Dialog open={isOpen} handler={setIsOpen}>
      <DialogHeader className="justify-between" data-testid="add-agami-to-exp-dialog-title">
        {t('dashboardExperienceDetail.addAgamiToExp')}
        <div className="flex items-center space-x-2">
          <Typography variant="small" className="font-bold">
            {t('dashboardExperienceDetail.uploadNewAgami')}
          </Typography>
          <IconButton
            size="sm"
            color={'primary' as colors}
            className="rounded-full w-6 h-6"
            onClick={() =>
              navigate(newAgamiPath(currentExpId), {
                state: { returnTo: `/experiences/${currentExpId}` },
              })
            }
            data-testid="upload-new-agami-btn"
          >
            <AiOutlinePlus className="w-4 h-4" />
          </IconButton>
        </div>
      </DialogHeader>
      <DialogBody className="overflow-y-scroll h-[90vh]">
        {isLoading && (
          <Center>
            <Spinner className="h-6 w-6 my-4" />
          </Center>
        )}

        {!isLoading && agamiList.length === 0 && (
          <span>
            You didn't create any <em>agami</em> yet.
          </span>
        )}

        {!isLoading && agamiList.length > 0 && (
          <AddAgamiToExperienceTable
            currentExpId={currentExpId}
            refetchAfterChange={refetchAfterChange}
            agamiList={agamiList}
          />
        )}
      </DialogBody>
    </Dialog>
  )
}
